import { format } from "date-fns";
import lowercase from "lodash.lowercase";
import type {
  AgenticLogicRuleRunDto,
  AgenticLogicRuleRunListItemDto,
} from "@intelligems/ig-types";
import { IgChip, IgNoRowsOverlay, IgTableWrapper } from "@intelligems/ig-theme";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import {
  createSharedTableDefaultColDef,
  sharedTableGridSx,
  sharedTableTheme,
} from "@src/features/sharedExperience/components/SharedTableCard";

import { SURFACE_LABEL } from "../../rules/surfaceLabels";

interface RecentRunsGridProps {
  runs: AgenticLogicRuleRunListItemDto[];
  loading: boolean;
  onRowClick: (run: AgenticLogicRuleRunListItemDto) => void;
}

const DEFAULT_COL_DEF =
  createSharedTableDefaultColDef<AgenticLogicRuleRunListItemDto>(
    {
      sortable: true,
      suppressMovable: false,
    },
    {
      fontSize: "11px",
    }
  );

const OUTCOME_COLOR: Record<
  AgenticLogicRuleRunDto["outcome"],
  "success" | "default" | "error"
> = {
  result: "success",
  empty: "default",
  error: "error",
};

const dash = (value: unknown) =>
  value === undefined || value === null || value === "" ? "—" : String(value);

const OutcomeCell = ({
  value,
}: {
  value?: AgenticLogicRuleRunDto["outcome"];
}) =>
  value ? (
    <IgChip
      size="small"
      sx={{ fontSize: "10px" }}
      color={OUTCOME_COLOR[value]}
      label={value}
    />
  ) : null;

const COLUMN_DEFS: ColDef<AgenticLogicRuleRunListItemDto>[] = [
  {
    headerName: "Evaluated",
    field: "evaluatedAtMs",
    minWidth: 170,
    valueFormatter: ({ value }) =>
      typeof value === "number" ? format(new Date(value), "PP p") : "—",
  },
  {
    headerName: "Outcome",
    field: "outcome",
    minWidth: 100,
    cellRenderer: OutcomeCell,
  },
  {
    headerName: "Surface",
    field: "surface",
    minWidth: 110,
    valueFormatter: ({ value }) =>
      lowercase(
        value ? SURFACE_LABEL[value as AgenticLogicRuleRunDto["surface"]] : "—"
      ),
  },
  {
    headerName: "Tier",
    field: "executionTier",
    minWidth: 90,
    valueFormatter: ({ value }) => dash(value),
  },
  {
    headerName: "Duration",
    field: "durationMs",
    minWidth: 100,
    valueFormatter: ({ value }) =>
      typeof value === "number" ? `${value} ms` : "—",
  },
  {
    headerName: "Version",
    field: "ruleVersion",
    minWidth: 90,
    valueFormatter: ({ value }) =>
      typeof value === "number" ? `v${value}` : "—",
  },
  {
    headerName: "Branch",
    field: "branchFired",
    minWidth: 110,
    valueFormatter: ({ value }) => dash(value),
  },
  {
    headerName: "Fallback",
    field: "fallback",
    minWidth: 120,
    valueFormatter: ({ value }) => dash(lowercase(value)),
  },
  {
    headerName: "Error",
    field: "errorClass",
    minWidth: 120,
    valueFormatter: ({ value }) => dash(lowercase(value)),
  },
  {
    headerName: "Items",
    field: "itemCount",
    minWidth: 80,
    valueFormatter: ({ value }) => dash(value),
  },
];

/** Recorded executions, newest first; the whole row opens the run's detail. */
export const RecentRunsGrid = ({
  runs,
  loading,
  onRowClick,
}: RecentRunsGridProps) => {
  const handleRowClicked = ({
    data,
  }: RowClickedEvent<AgenticLogicRuleRunListItemDto>) => {
    if (data) onRowClick(data);
  };

  return (
    <IgTableWrapper
      minHeight={null}
      sx={[
        sharedTableGridSx,
        {
          backgroundColor: "white",
          overflowX: "auto",
          "& .ag-root-wrapper": { minWidth: 1100 },
          "& .ag-row": { cursor: "pointer" },
        },
      ]}
    >
      <AgGridReact<AgenticLogicRuleRunListItemDto>
        columnDefs={COLUMN_DEFS}
        domLayout="autoHeight"
        defaultColDef={DEFAULT_COL_DEF}
        getRowId={({ data }) => data.invocationId}
        loading={loading}
        noRowsOverlayComponent={IgNoRowsOverlay}
        noRowsOverlayComponentParams={{ message: "No recent runs" }}
        onRowClicked={handleRowClicked}
        rowHeight={44}
        rowData={runs}
        suppressCellFocus
        theme={sharedTableTheme}
      />
    </IgTableWrapper>
  );
};
